import { useState, useRef, useEffect } from "react";

const MIN_SEARCH_LENGTH = 2;
const MAX_SEARCH_LENGTH = 100;

interface UseConversationSearchProps {
  setSearchQuery: (query: string) => void;
  onSearch: (query: string) => void;
}

export const useConversationSearch = ({ setSearchQuery, onSearch }: UseConversationSearchProps) => {
  const [isOpenSearchInfo, setIsOpenSearchInfo] = useState<boolean>(false);
  const [searchInvalid, setSearchInvalid] = useState<boolean>(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const validateSearch = (value: string): boolean => {
    if (value.length === 0) {
      return false;
    }
    if (value.length < MIN_SEARCH_LENGTH) {
      return true;
    }
    if (value.length > MAX_SEARCH_LENGTH) {
      return true;
    }
    return false;
  };

  const handleSearch = (value: string) => {
    const searchValue = value.trim();
    const isInvalid = validateSearch(searchValue);
    setSearchInvalid(isInvalid);
    if (isInvalid) {
      setIsOpenSearchInfo(true);
      return;
    }
    setSearchQuery(searchValue);
    onSearch(searchValue);
  };

  const handleClear = () => {
    setSearchInvalid(false);
    setIsOpenSearchInfo(false);
    setSearchQuery("");
    onSearch("");
  };

  const toggleSearchInfo = () => {
    setIsOpenSearchInfo(prev => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setIsOpenSearchInfo(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, []);

  return {
    MIN_SEARCH_LENGTH,
    MAX_SEARCH_LENGTH,
    isOpenSearchInfo,
    searchInvalid,
    popoverRef,
    handleSearch,
    handleClear,
    toggleSearchInfo,
  };
};

